"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { ArrowRight, Calendar as CalendarIcon, Check } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useState } from "react";
import { Link } from "@/components/common/Link";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const agencies = [{
    id: "fbi",
    label: "FBI"
  }, {
    id: "interpol",
    label: "Interpol"
  }, {
    id: "local_police",
    label: "Local Police"
  }, {
    id: "sec",
    label: "SEC"
  }, {
    id: "cftc",
    label: "CFTC"
  }, {
    id: "crypto_exchange",
    label: "Crypto Exchange"
  }, {
    id: "other",
    label: "Other"
  }];
  if (isSubmitted && !submitError) {
    return <div className="text-center py-16 px-8">
        <p className="font-default text-xs tracking-[0.25em] uppercase text-slate-400 mb-6">— VERIFIED —</p>
        <h3 className="font-default text-3xl md:text-4xl text-white mb-6 leading-tight">
          Request <em>processed</em>.
        </h3>
        <p className="font-default italic text-lg text-slate-400 max-w-md mx-auto mb-8">
          For security reasons, please contact <Link to="mailto:forensics@cryptoscamdatabase.com" className="text-blue-500 hover:underline transition-colors">forensics@cryptoscamdatabase.com</Link>
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-lg px-6 bg-blue-600 text-white border-0">
          New Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="case_reference" rules={{
      required: "Case reference is required"
    }} render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="font-default text-[10px] tracking-[0.25em] uppercase text-slate-400">
              Case Reference ID
            </FormLabel>
            <FormControl>
              <Input placeholder="TX-0000-0000" {...field} className="border-0 border-b border-slate-700 bg-transparent px-0 h-11 font-default text-lg rounded-lg focus-visible:ring-0 focus-visible:border-blue-500 placeholder:text-slate-600" />
            </FormControl>
            <FormMessage className="font-default italic text-red-500" />
          </FormItem>} />

      <FormField control={control} name="incident_date" rules={{
      required: "Incident date is required"
    }} render={({
      field
    }) => <FormItem className="gap-1 flex flex-col">
            <FormLabel className="font-default text-[10px] tracking-[0.25em] uppercase text-slate-400">
              When did the incident occur?
            </FormLabel>
            <Popover>
              <PopoverTrigger asChild>
                <FormControl>
                  <Button variant={"outline"} className={cn("w-full pl-0 text-left font-default text-lg h-11 border-0 border-b border-slate-700 bg-transparent rounded-lg hover:bg-transparent hover:border-blue-500 focus:border-blue-500", !field.value && "text-slate-600")}>
                    {field.value ? format(new Date(field.value), "PPP") : <span>Select date</span>}
                    <SafeHydrate><CalendarIcon className="ml-auto h-4 w-4 opacity-50" /></SafeHydrate>
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-800" align="start">
                <SafeHydrate><Calendar mode="single" selected={field.value ? new Date(field.value) : undefined} onSelect={date => field.onChange(date?.toISOString())} disabled={date => date > new Date() || date < new Date("1900-01-01")} initialFocus className="bg-slate-900 text-white" /></SafeHydrate>
              </PopoverContent>
            </Popover>
            <FormMessage className="font-default italic text-red-500" />
          </FormItem>} />

      <FormField control={control} name="reported_to" render={() => <FormItem className="gap-3">
            <div className="mb-2">
              <FormLabel className="font-default text-[10px] tracking-[0.25em] uppercase text-slate-400">
                Agencies Reported To
              </FormLabel>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {agencies.map(agency => <FormField key={agency.id} control={control} name="reported_to" render={({
          field
        }) => {
          return <FormItem key={agency.id} className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox className="border-slate-700 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 rounded-lg" checked={field.value?.includes(agency.id)} onCheckedChange={checked => {
                return checked ? field.onChange([...field.value, agency.id]) : field.onChange(field.value?.filter((value: string) => value !== agency.id));
              }} />
                        </FormControl>
                        <FormLabel className="font-default text-sm text-slate-300 font-normal cursor-pointer">
                          {agency.label}
                        </FormLabel>
                      </FormItem>;
        }} />)}
            </div>
            <FormMessage className="font-default italic text-red-500" />
          </FormItem>} />

      {submitError && <div className="p-4 border border-red-500/30 rounded-lg bg-red-950/20">
          <p className="font-default italic text-red-400">{submitError}</p>
        </div>}

      <div className="pt-6 space-y-4">
        <Button type="submit" disabled={isSubmitting} className="rounded-lg bg-blue-600 text-white hover:bg-blue-700 px-7 h-12 font-default text-xs tracking-[0.2em] uppercase">
          {isSubmitting ? "Decrypting..." : "Verify Case"}
          <ArrowRight className="size-4 ml-1" />
        </Button>
        <p className="font-default italic text-sm text-slate-500">
          All inquiries are encrypted and handled via secure channels.
        </p>
      </div>
    </Form>;
}
export default function SecureInquiry() {
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const formCard = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const clocks = [{
    city: "New York",
    time: "14:00 EST"
  }, {
    city: "Dubai",
    time: "22:00 GST"
  }, {
    city: "Sydney",
    time: "05:00 AEDT"
  }, {
    city: "Cape Town",
    time: "20:00 SAST"
  }];
  return <section data-section-id="4818" id="secure-inquiry" className="bg-slate-950 py-24 md:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div ref={intro.ref} className={`lg:col-span-5 lg:pt-6 transition-all duration-1000 ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <h1 className="font-default text-5xl md:text-6xl lg:text-[4rem] leading-[1.05] text-white mb-10">
              Blockchain<br />
              <em className="text-blue-500">Forensics</em> Portal.
            </h1>

            <p className="font-default text-lg md:text-xl text-slate-300 leading-relaxed mb-12 max-w-md">
              Secure access to our global ledger investigation suite. Verify your case reference to initiate the decryption and analysis protocol.
            </p>

            <div>
              <p className="font-default text-[10px] tracking-[0.3em] uppercase text-slate-500 mb-5">
                Global Operations
              </p>
              <div className="grid grid-cols-2 gap-4 max-w-sm">
                {clocks.map((c, i) => <div data-index={i} key={i} className="p-4 border border-slate-800 bg-slate-900 rounded-lg">
                    <span className="block font-default text-[10px] text-slate-500 uppercase">{c.city}</span>
                    <span className="block font-default text-lg text-white">{c.time}</span>
                  </div>)}
              </div>
            </div>
          </div>

          <div ref={formCard.ref} className={`lg:col-span-7 lg:col-start-6 transition-all duration-1000 delay-150 ease-out ${formCard.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <FormProvider formKey="secure_inquiry_intelligence_hub" formId="secure_inquiry_intelligence_hub" sectionName="secure-inquiry" className="bg-slate-900 rounded-lg p-8 md:p-12 lg:p-14 shadow-lg border border-slate-800" defaultValues={{
            case_reference: "",
            incident_date: "",
            reported_to: []
          }} validationRules={{
            case_reference: {
              required: true
            },
            incident_date: {
              required: true,
              custom: val => {
                if (!val) return "Date is required";
                const date = new Date(val);
                if (date > new Date()) return "Incident date must be in the past";
                return null;
              }
            }
          }} submitText="Verify Case" successMessage="For security reasons, please contact forensics@cryptoscamdatabase.com" errorMessage="Invalid reference. Please verify your credentials.">
              <p className="font-default text-[10px] tracking-[0.3em] uppercase text-blue-500 mb-2">
                Secure Access
              </p>
              <h2 className="font-default text-3xl md:text-4xl text-white mb-10 leading-tight">
                Enter <em>Case Reference</em>.
              </h2>
              <ConsultationForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}